import React from 'react';
import { cva, VariantProps } from 'class-variance-authority';

import { cn } from '@epoch-ui/lib/utils';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose
} from '@epoch-ui/components/Dialog/dialog';

/**
 * Modal component built on top of <Dialog /> component from @epoch-ui library.
 *
 * All components are imported and re-exported from `@epoch-ui/components/Dialog`
 * with applying specified styles for Modal component in the Design System specs in Figma.
 */

export const Modal = Dialog;
Modal.displayName = 'Modal';

export const ModalTrigger = DialogTrigger;
ModalTrigger.displayName = 'ModalTrigger';

const modalContentVariants = cva(
  'tw-p-6 tw-rounded-[16px] sm:tw-rounded-[16px] tw-m-auto tw-w-[calc(100%-32px)] tw-max-h-[calc(100vh-32px)] tw-gap-0',
  {
    variants: {
      size: {
        sm: 'tw-max-w-[400px]',
        md: 'tw-max-w-[560px]',
        lg: 'tw-max-w-[688px]',
        xlg: 'tw-max-w-[896px]'
      }
    },
    defaultVariants: {
      size: 'sm'
    }
  }
);

export interface ModalContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogContent>,
    VariantProps<typeof modalContentVariants> {}

export const ModalContent = React.forwardRef<
  React.ElementRef<typeof DialogContent>,
  ModalContentProps
>(({ className, size, ...props }, ref) => (
  <DialogContent ref={ref} className={cn(modalContentVariants({ size, className }))} {...props} />
));
ModalContent.displayName = 'ModalContent';

export const ModalHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <DialogHeader className={cn('tw-mb-4 tw-flex tw-flex-col tw-text-left', className)} {...props} />
);
ModalHeader.displayName = 'ModalHeader';

export const ModalTitle = React.forwardRef<
  React.ElementRef<typeof DialogTitle>,
  React.ComponentPropsWithoutRef<typeof DialogTitle>
>(({ className, ...props }, ref) => (
  <DialogTitle
    ref={ref}
    className={cn('tw-m-0 tw-mr-10 tw-text-2xl tw-font-medium tw-leading-8', className)}
    {...props}
  />
));
ModalTitle.displayName = 'ModalTitle';

export const ModalDescription = React.forwardRef<
  React.ElementRef<typeof DialogDescription>,
  React.ComponentPropsWithoutRef<typeof DialogDescription>
>(({ className, ...props }, ref) => (
  <DialogDescription
    ref={ref}
    className={cn(
      'tw-m-0 tw-mt-1 tw-text-sm tw-font-normal tw-leading-5 tw-text-primary-grey',
      className
    )}
    {...props}
  />
));
ModalDescription.displayName = 'ModalDescription';

export const ModalFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <DialogFooter
    className={cn(
      'tw-mt-6 tw-flex tw-flex-row tw-items-center tw-justify-end tw-gap-3 sm:tw-justify-end',
      className
    )}
    {...props}
  />
);
ModalFooter.displayName = 'ModalFooter';

export const ModalClose = DialogClose;
ModalClose.displayName = 'ModalClose';

export interface ModalImageProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
}

export const ModalImage = React.forwardRef<HTMLDivElement, ModalImageProps>(
  ({ className, src, alt = '', ...props }, ref) => (
    <div ref={ref} className={cn('tw--mx-6 tw--mt-6 tw-mb-6 tw-max-h-72', className)} {...props}>
      {src ? (
        <img src={src} alt={alt} className="tw-h-full tw-w-full tw-object-cover" />
      ) : (
        <div className="tw-flex tw-aspect-[16/9] tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-muted">
          <svg
            className="tw-h-12 tw-w-12 tw-text-muted-foreground"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </div>
      )}
    </div>
  )
);
ModalImage.displayName = 'ModalImage';
